import { useCallback, useEffect, useState } from 'react';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import { VoiceoverRegistry } from 'js/src/agent/registries/VoiceoverRegistry';
import type { UploadedAudio, UploadedFile } from 'js/src/components/sketch/FileUpload';
import { isUploadedAudio, isUploadedMedia } from 'js/src/components/sketch/FileUpload';
import { ErrorReporter } from 'js/src/libs/error-reporter';

const CHAT_HISTORY_KEY = 'chat-last-input';

export interface ChatHistoryEntry {
    text: string;
    files: UploadedFile[];
}

/**
 * Load last chat message from localStorage
 */
function loadLastEntry(): ChatHistoryEntry | null {
    try {
        const stored = localStorage.getItem(CHAT_HISTORY_KEY);
        if (stored === null) {
            return null;
        }
        try {
            const parsed = JSON.parse(stored) as unknown;
            if (parsed && typeof parsed === 'object' && 'text' in parsed) {
                const entry = parsed as ChatHistoryEntry;
                return { text: entry.text ?? '', files: entry.files ?? [] };
            }
        } catch {
            // Fall through: legacy entries were stored as a bare text string
        }
        return { text: stored, files: [] };
    } catch {
        return null;
    }
}

/**
 * Save last chat message to localStorage
 */
function saveLastEntry(entry: ChatHistoryEntry): void {
    try {
        localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(entry));
    } catch {
        // Silently fail if localStorage is not available
    }
}

/**
 * Re-register recalled media and audio with their registries by UUID. The persisted
 * `mediaRegistryId` / `voiceoverRegistryId` are session-local handles into in-memory
 * singletons that are empty on a fresh page load, so they must be refreshed from the
 * UUID before the chat can reference the asset. Media and audio that cannot be re-registered
 * are dropped rather than recalled with a dead id. Documents pass through unchanged.
 */
async function rehydrateRecalledFiles(files: UploadedFile[]): Promise<UploadedFile[]> {
    const mediaRegistry = MediaRegistry.getInstance();
    const voiceoverRegistry = VoiceoverRegistry.getInstance();
    const rehydrated: UploadedFile[] = [];

    for (const file of files) {
        // Documents carry their own text and url, so they need no registry entry.
        if (!isUploadedMedia(file) && !isUploadedAudio(file)) {
            rehydrated.push(file);
            continue;
        }
        // The uuid is the only durable handle on the asset. Without it the stale id cannot be
        // refreshed, and keeping it would hand the agent an id that resolves to nothing.
        if (!file.uuid) {
            continue;
        }
        try {
            if (isUploadedAudio(file)) {
                // Mirrors the upload path: non-voiceover audio goes to the MusicRegistry
                // and carries no voiceover id.
                const resolved = await voiceoverRegistry.addUploadedAudioIfVoiceover(file.uuid);
                if (!resolved) {
                    // The asset is gone — drop the file.
                    continue;
                }
                if (!resolved.hasVoiceover) {
                    MusicRegistry.getInstance().addItem(resolved.assetData);
                }
                rehydrated.push({
                    ...file,
                    voiceoverRegistryId: resolved.registryId ?? undefined,
                    has_voiceover: resolved.hasVoiceover,
                    voiceover_transcript: resolved.voiceoverTranscript,
                } as UploadedAudio);
                continue;
            }

            const mediaRegistryId = await mediaRegistry.addItemByUuid(file.uuid);
            if (mediaRegistryId) {
                rehydrated.push({ ...file, mediaRegistryId });
            }
            // If addItemByUuid returns null the asset is gone — drop the file.
        } catch (error) {
            // Partial success is better than dropping the whole recall.
            if (error instanceof Error) {
                ErrorReporter.capture(error);
            }
        }
    }

    return rehydrated;
}

interface UseChatHistoryOptions {
    onHistoryChange: (entry: ChatHistoryEntry) => void;
}

interface UseChatHistoryReturn {
    recallLast: () => void;
    saveToHistory: (message: string, files?: UploadedFile[]) => void;
}

/**
 * Hook for managing chat history - stores only the last message
 */
export function useChatHistory({ onHistoryChange }: UseChatHistoryOptions): UseChatHistoryReturn {
    const [lastEntry, setLastEntry] = useState<ChatHistoryEntry | null>(null);

    // Load last message on mount
    useEffect(() => {
        setLastEntry(loadLastEntry());
    }, []);

    const recallLast = useCallback(() => {
        if (!lastEntry) {
            return;
        }
        if (lastEntry.files.length === 0) {
            onHistoryChange(lastEntry);
            return;
        }
        void rehydrateRecalledFiles(lastEntry.files).then((files) => {
            onHistoryChange({ text: lastEntry.text, files });
        });
    }, [lastEntry, onHistoryChange]);

    const saveToHistory = useCallback((message: string, files: UploadedFile[] = []) => {
        if (!message.trim() && files.length === 0) {
            return;
        }
        const entry: ChatHistoryEntry = { text: message, files };
        saveLastEntry(entry);
        setLastEntry(entry);
    }, []);

    return {
        recallLast,
        saveToHistory,
    };
}
